import { useState } from 'react';
import { Badge, Button, Card, Coin, SectionHeading } from '@/components/ui';
import PayDialog from '@/components/wallet/PayDialog';
import { useAuth } from '@/lib/auth';
import {
  useWallet,
  useCoinPacks,
  createOrder,
  rupees,
  type CoinPack,
  type CreatedOrder,
  type PaymentOrder,
} from '@/lib/data/wallet';

export default function Wallet() {
  const { profile } = useAuth();
  const { wallet, loading, error, refresh } = useWallet();

  // Only a companion sees the companion fee. A consumer being told about a
  // charge that cannot apply to them is noise.
  const isCompanion = profile?.role === 'companion' || profile?.role === 'admin';
  const packs = useCoinPacks();

  const [pending, setPending] = useState<CreatedOrder | null>(null);
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);

  async function buy(pack: CoinPack) {
    setBusyPack(pack.code);
    setOrderError(null);
    try {
      setPending(await createOrder({ purpose: 'coin_purchase', packCode: pack.code }));
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : 'Could not start the payment.');
    }
    setBusyPack(null);
  }

  async function payFee(purpose: 'user_platform_fee' | 'companion_platform_fee') {
    setOrderError(null);
    try {
      const order = await createOrder({ purpose });

      // A zero fee is settled server-side without a checkout — there is
      // nothing to confirm, so skip the dialog rather than showing "Pay ₹0".
      if (order.orderId === 'waived') {
        await refresh();
        setFlash('No fee is charged for that right now.');
        return;
      }
      setPending(order);
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : 'Could not start the payment.');
    }
  }

  async function onPaid(coinsCredited: number) {
    setPending(null);
    await refresh();
    setFlash(
      coinsCredited > 0
        ? `${coinsCredited.toLocaleString('en-IN')} coins added.`
        : 'Payment complete.',
    );
    setTimeout(() => setFlash(null), 4000);
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-40 animate-pulse rounded-2xl" style={{ background: 'var(--surface-2)' }} />
        <div className="h-64 animate-pulse rounded-2xl" style={{ background: 'var(--surface-2)' }} />
      </div>
    );
  }

  if (error || !wallet) {
    return (
      <Card className="px-6 py-14 text-center">
        <h1 className="text-xl font-semibold">Could not load your wallet</h1>
        <p className="mt-2 text-sm text-[var(--ink-2)]">{error}</p>
        <div className="mt-6">
          <Button onClick={() => void refresh()}>Try again</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-14">
      {/* ═══ Balance ════════════════════════════════════════════════════════ */}
      <section className="animate-rise">
        <p className="eyebrow">Wallet</p>
        <h1 className="mt-2 text-4xl font-semibold">Your coins</h1>

        {wallet.mockPayments && (
          <p
            className="mt-4 rounded-xl px-4 py-3 text-sm"
            style={{ background: 'var(--marigold-soft)', color: 'var(--marigold)' }}
          >
            <strong>Test mode.</strong> Payments are simulated — coins are credited without
            charging anything. The real gateway is connected before launch.
          </p>
        )}

        <Card raised className="mt-6 overflow-hidden">
          <div
            className="px-7 py-9"
            style={{
              background:
                'linear-gradient(135deg, var(--marigold-soft), color-mix(in srgb, var(--marigold-soft) 40%, var(--surface)))',
            }}
          >
            <p className="text-sm text-[var(--ink-2)]">Balance</p>
            <p className="mt-1 flex items-center gap-2.5 text-5xl font-semibold tabular-nums">
              <Coin className="text-[var(--marigold)]" />
              {wallet.balanceCoins.toLocaleString('en-IN')}
            </p>
            <p className="mt-3 text-sm text-[var(--ink-2)]">
              {wallet.purchasedCoins.toLocaleString('en-IN')} purchased
              {wallet.bonusCoins > 0 && (
                <> · {wallet.bonusCoins.toLocaleString('en-IN')} bonus, spent first</>
              )}
            </p>
          </div>
        </Card>

        {flash && (
          <p className="mt-4 text-sm font-medium" style={{ color: 'var(--teal)' }}>
            {flash}
          </p>
        )}
        {orderError && (
          <p className="mt-4 text-sm" style={{ color: 'var(--danger)' }}>
            {orderError}
          </p>
        )}
      </section>

      {/* ═══ Platform fees ══════════════════════════════════════════════════ */}
      {/*
        There are TWO fees and they are not interchangeable:
          · the member fee lets you BOOK other people
          · the companion fee lets you BE BOOKED
        Someone who does both pays both, which is why they are separate rows in
        platform_fee_payments rather than one flag. This card used to say only
        "One-time platform fee — it unlocks booking", which read to a companion
        as the fee for being a companion.
      */}
      {(!wallet.fees.userFeePaid && wallet.fees.userFeePaise > 0) ||
      (isCompanion && !wallet.fees.companionFeePaid && wallet.fees.companionFeePaise > 0) ? (
        <section className="animate-rise">
          <SectionHeading eyebrow="One-time">Platform fees</SectionHeading>

          <div className="flex flex-col gap-4">
            {!wallet.fees.userFeePaid && wallet.fees.userFeePaise > 0 && (
              <Card className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">Member fee — to book other people</h3>
                    <p className="mt-1 max-w-md text-sm text-[var(--ink-2)]">
                      Paid once, never again. Coins are separate and cover what you actually use.
                      {isCompanion && ' Being a companion does not cover this — it is the fee for booking others.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-semibold">
                      {rupees(wallet.fees.userFeePaise)}
                    </span>
                    <Button onClick={() => void payFee('user_platform_fee')}>Pay now</Button>
                  </div>
                </div>
              </Card>
            )}

            {isCompanion &&
              !wallet.fees.companionFeePaid &&
              wallet.fees.companionFeePaise > 0 && (
                <Card className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold">Companion fee — to be booked</h3>
                      <p className="mt-1 max-w-md text-sm text-[var(--ink-2)]">
                        Paid once when you start offering services. Separate from the member fee
                        above.
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-semibold">
                        {rupees(wallet.fees.companionFeePaise)}
                      </span>
                      <Button onClick={() => void payFee('companion_platform_fee')}>Pay now</Button>
                    </div>
                  </div>
                </Card>
              )}
          </div>
        </section>
      ) : null}

      {/* ═══ Packs ══════════════════════════════════════════════════════════ */}
      <section className="animate-rise">
        <SectionHeading eyebrow="Top up">Add coins</SectionHeading>

        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {packs.map((pack) => {
            const total = pack.coins + pack.bonus_coins;
            return (
              <Card
                as="li"
                key={pack.code}
                className="flex flex-col p-5 transition-all duration-200 hover:-translate-y-0.5"
              >
                {pack.tag ? (
                  <Badge tone="marigold">{pack.tag}</Badge>
                ) : (
                  <span className="h-[26px]" aria-hidden="true" />
                )}

                <p className="mt-3 flex items-center gap-2 text-2xl font-semibold tabular-nums">
                  <Coin className="text-[var(--marigold)]" />
                  {total.toLocaleString('en-IN')}
                </p>

                <p className="mt-1 text-sm text-[var(--ink-2)]">
                  {pack.bonus_coins > 0 ? (
                    <>
                      {pack.coins.toLocaleString('en-IN')} +{' '}
                      <span className="text-[var(--teal)]">
                        {pack.bonus_coins.toLocaleString('en-IN')} bonus
                      </span>
                    </>
                  ) : (
                    'no bonus'
                  )}
                </p>

                <p className="mt-4 text-xl font-semibold">{rupees(pack.price_paise)}</p>

                <div className="mt-4">
                  <Button
                    full
                    onClick={() => void buy(pack)}
                    disabled={busyPack === pack.code}
                    variant={pack.tag ? 'primary' : 'secondary'}
                  >
                    {busyPack === pack.code ? 'Starting…' : 'Buy'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </ul>
      </section>

      {/* ═══ History ════════════════════════════════════════════════════════ */}
      <section className="animate-rise">
        <SectionHeading eyebrow="History">Recent payments</SectionHeading>

        {wallet.orders.length === 0 ? (
          <Card className="px-6 py-10 text-center">
            <p className="text-sm text-[var(--ink-2)]">Nothing yet.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <ul>
              {wallet.orders.map((o, i) => (
                <OrderRow key={o.id} order={o} first={i === 0} />
              ))}
            </ul>
          </Card>
        )}
      </section>

      {pending && (
        <PayDialog
          order={pending}
          onDone={(c) => void onPaid(c)}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

const PURPOSE_LABEL: Record<PaymentOrder['purpose'], string> = {
  coin_purchase: 'Coins',
  user_platform_fee: 'Platform fee',
  companion_platform_fee: 'Companion platform fee',
};

function OrderRow({ order, first }: { order: PaymentOrder; first: boolean }) {
  const total = (order.coins ?? 0) + order.bonus_coins;

  return (
    <li
      className="flex items-center justify-between gap-4 px-5 py-4"
      style={first ? undefined : { borderTop: '1px solid var(--line-soft)' }}
    >
      <div className="min-w-0">
        <p className="font-medium">
          {PURPOSE_LABEL[order.purpose]}
          {total > 0 && (
            <span className="ml-2 text-sm font-normal text-[var(--ink-2)]">
              +{total.toLocaleString('en-IN')} coins
            </span>
          )}
        </p>
        <p className="mt-0.5 text-xs text-[var(--muted)]">
          {new Date(order.created_at).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
          {order.gateway === 'mock' && ' · test payment'}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span className="font-semibold tabular-nums">{rupees(order.amount_paise)}</span>
        <Badge tone={order.status === 'paid' ? 'teal' : 'neutral'}>{order.status}</Badge>
      </div>
    </li>
  );
}
